<template>
	<div class="tempworker-document-upload">
		<o-section
			:title="tempWorkerDocumentEntity.title"
			:subtitle="tempWorkerDocumentEntity.isNew ? `Ajouter un document` : `Modifier le document`">
			<o-card
				v-if="tempWorkerDocumentEntity && tempWorkerDocumentEntity.id && setup.showPreview"
				class="preview">
				<img
					:src="`/app/tempworker/tempworkerdocument/${tempWorkerDocumentEntity.id}/preview`">
				<!-- @error="useMissingPreview" -->
				<o-button
					color="info"
					prefix-font-icon="icon-interface-edit-pencil"
					outline
					@click="setup.showPreview = false">
					Modifier le fichier
				</o-button>
			</o-card>

			<template v-else>
				<div
					v-if="tempWorkerDocumentEntity.typeLabel === 'IdentityPiece' || tempWorkerDocumentEntity.typeLabel === 'DriverLicence'"
					class="toggle-recto-verso">
					{{ setup.docRectoVerso ? `Désactiver` : `Activer` }} le mode recto/verso
					<o-toggle v-model="setup.docRectoVerso"/>
				</div>

				<div
					class="section-upload"
					:class="{ 'section-upload--multiple': setup.docRectoVerso }">
					<o-upload
						ref="recto"
						v-model="setup.files"
						:file-types="setup.fileTypes">
						<span v-html="setup.labelFieldUpload"/>
					</o-upload>
					<o-upload
						v-if="setup.docRectoVerso"
						v-model="setup.filesVerso"
						:file-types="setup.fileTypes">
						<span v-html="setup.labelFieldUploadVerso"/>
					</o-upload>
				</div>
			</template>
		</o-section>

		<o-section>
			<o-select
				v-if="tempWorkerDocumentEntity.typeLabel === 'IdentityPiece'"
				ref="identityPieceTypeSelect"
				v-model="tempWorkerDocumentEntity.identityPieceType"
				label="Type de pièce d'identité"
				:options="setup.useTempWorkerDocumentService.identityPieceTypeOptions"
				display-key="label"
				value-key="value"
				track-key="value"
				required/>

			<o-datepicker
				v-if="tempWorkerDocumentEntity.showExpirationDate"
				v-model="tempWorkerDocumentEntity.expirationDate"
				:min-date="setup.useTempWorkerDocumentService.tommorow"
				label="Date d'expiration"/>

			<o-select
				v-if="tempWorkerDocumentEntity.typeLabel === 'DriverLicence'"
				v-model="tempWorkerDocumentEntity.selectedDriverLicences"
				label="Catégories de permis"
				multiple
				:options="setup.useTempWorkerDocumentService.driverLicenceOptions">
				<template
					v-if="tempWorkerDocumentEntity.driverLicences"
					#value="{ item }">
					<span class="select-driver-licence-item">
						{{ item }}
						<img
							:src="setup.dynamicImg(`DriverLicencePicto${item}Svg`)"
							:alt="item">
					</span>
				</template>
				<template #option="{ item }">
					<span class="select-driver-licence-item">
						<span>{{ item }}</span>
						<img
							:src="setup.dynamicImg(`DriverLicencePicto${item}Svg`)"
							:alt="item">
					</span>
				</template>
			</o-select>

			<div
				v-if="tempWorkerDocumentEntity.typeLabel === 'DriverLicence'"
				class="row row--gutter">
				<div
					v-for="licence in tempWorkerDocumentEntity.driverLicences"
					:key="licence.label"
					class="col-sm-6">
					<o-datepicker
						v-model="licence.obtainingDate"
						:label="`Date d'obtention du permis ${licence.label}`"
						@input="tempWorkerDocumentEntity.setDriverLicenceObtainingDate(licence)"/>
				</div>
			</div>

			<o-datepicker
				v-if="tempWorkerDocumentEntity.showObtainingDate"
				v-model="tempWorkerDocumentEntity.documentObtainingDate"
				label="Date d'obtention"/>

			<o-input
				v-if="tempWorkerDocumentEntity.showDocumentTitle"
				v-model.trim="tempWorkerDocumentEntity.documentTitle"
				label="Nom du fichier"
				required/>

			<o-alert
				v-if="setup.errorMessage && setup.displayErrorMessage"
				color="danger"
				center>
				{{ setup.errorMessage }}
			</o-alert>
		</o-section>

		<o-section align="stretch">
			<div class="upload-actions">
				<o-pop-confirm
					v-if="!tempWorkerDocumentEntity.isNew"
					class="upload-actions__button"
					@confirm="setup.deleteDoc()">
					<o-button
						outline
						color="danger"
						prefix-font-icon="icon-interface-delete-bin-2">
						Supprimer
					</o-button>
				</o-pop-confirm>
				<o-button
					v-show="setup.showAddOrUpdateButton"
					class="upload-actions__button"
					:color="tempWorkerDocumentEntity.isNew ? 'success' : 'info'"
					prefix-font-icon="icon-interface-validation-check"
					@click="tempWorkerDocumentEntity.isNew ? setup.uploadDoc() : setup.updateDocAsync()">
					{{ tempWorkerDocumentEntity.isNew ? `Ajouter` : `Enregistrer` }}
				</o-button>
			</div>
		</o-section>
	</div>
</template>

<script setup lang="ts">
import { inject } from 'vue';
import UserProfileTempWorkerDocumentAsideUploadSetupService from '@/setup/user-profile/UserProfileTempWorkerDocumentAsideUploadSetupService';
const _aside = inject<OrionAside>('_aside');
const props = defineProps(UserProfileTempWorkerDocumentAsideUploadSetupService.props);
const setup = new UserProfileTempWorkerDocumentAsideUploadSetupService(props, _aside);
</script>

<style lang="less" scoped>
@import '@/styles/variables';

.preview {
	width: 100%;
	margin: auto;
	text-align: center;
	overflow: hidden;

	img {
		width: calc(100% + 2.5rem);
		max-width: none;
		max-height: 25rem;
		object-fit: contain;
		margin: -1.25rem -1.25rem 1.25rem;

		&.missing-document-preview {
			max-width: 180px;
			object-fit: contain;
			margin: 0 auto 1.25rem;
		}
	}
}

.toggle-recto-verso {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 0.5rem 0.7rem 0.5rem 1rem;
	text-transform: uppercase;
	font-weight: bold;
	font-size: 0.8rem;
	border: 0.06rem dashed var(--grey-light);
	border-radius: 0.3rem;
	margin-bottom: 1.25rem;
}

.section-upload {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;

	@media @phone {
		flex-direction: column;
	}

	.orion-upload {
		flex: 1 0 0%;

		@media @phone {
			width: 100%;
		}
	}

	&--multiple {
		.orion-upload {
			max-width: calc(50% - 0.6rem);
			padding: 0.6rem;

			@media @phone {
				width: 100%;
				max-width: none;
				margin: 0.6rem 0;
			}
		}
	}
}

.select-driver-licence-item {
	display: flex;
	align-items: center;

	> span {
		display: block;
		width: 1.9rem;
	}

	> img {
		display: block;
		max-height: 0.9rem;
		margin-top: 0.06rem;
		margin-left: 0.4rem;
		opacity: 0.6;
	}
}

.upload-actions {
	display: flex;
	gap: 1rem;
	width: 100%;

	&__button {
		display: flex;
		flex: 1;
	}
}

</style>

